import { HttpClient } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { FacebookService, InitParams, LoginResponse } from 'ngx-facebook';
import {
  FacebookConnection,
  FacebookPage,
} from 'projects/brn-frontend/src/app/core/store/bot-connections';
import { BehaviorSubject, Observable } from 'rxjs';

import { BotSelectors } from '../store/bot/bot.selectors';
import { PortalService } from './portal.service';

@Injectable({
  providedIn: 'root',
})
export class ConnectionsService {
  @SelectSnapshot(BotSelectors.botId)
  botId!: string;

  private fbPages$ = new BehaviorSubject<FacebookPage[]>([]);
  fbPages = this.fbPages$.asObservable();

  private fbAuthStatus$ = new BehaviorSubject<boolean>(false);
  fbAuthStatus = this.fbAuthStatus$.asObservable();

  private permissionScopes = {
    facebook:
      'public_profile,pages_messaging,pages_read_engagement,pages_manage_metadata',
    instagram:
      'instagram_basic,instagram_manage_messages,pages_manage_metadata,instagram_manage_comments',
  };

  constructor(
    private http: HttpClient,
    private fb: FacebookService,
    private snackbar: MatSnackBar,
    private zone: NgZone,
    public dialog: MatDialog
  ) {
    const initParams: InitParams = {
      // live app
      appId: '279474245766642',

      // test app
      // appId: '3589207444530966',
      xfbml: true,
      version: 'v3.2',
    };
    fb.init(initParams);
  }

  /* ------------------------ Main connections actions ------------------------ */
  createBotConnection(
    provider: string,
    providerData: any,
    botId?: string
  ): Observable<any> {
    return this.http.post(
      `${PortalService.ApiUrl}/connection/${provider}/${botId || this.botId}`,
      { provider_data: providerData }
    );
  }

  updateBotConnection(
    provider: string,
    providerData: any,
    botId?: string
  ): Observable<any> {
    return this.http.put(
      `${PortalService.ApiUrl}/connection/${provider}/${botId || this.botId}`,
      { provider_data: providerData }
    );
  }

  removeBotConnection(provider: string, botId?: string): Observable<any> {
    return this.http.delete(
      `${PortalService.ApiUrl}/connection/${provider}/${botId || this.botId}`
    );
  }

  getProvidersList(): Observable<any> {
    return this.http.get(
      PortalService.ApiUrl + '/connections/' + this.botId + '/providers'
    );
  }

  getFacebookPages(instagramPermission?: boolean): Observable<FacebookPage[]> {
    this.fbPages$.next([]);

    this.fb.getLoginStatus(true).then((status) => {
      if (status.status !== 'connected') {
        this.loginWithFacebook(
          this.getFacebookPermissionsScope(instagramPermission)
        );
      } else {
        this.fb.logout().then(() => {
          this.loginWithFacebook(
            this.getFacebookPermissionsScope(instagramPermission)
          );
        });
      }
    });
    return this.fbPages;
  }

  private getFacebookPermissionsScope(instagramPermission?: boolean): string {
    if (instagramPermission) {
      return this.permissionScopes.instagram;
    } else {
      return this.permissionScopes.facebook;
    }
  }

  loginWithFacebook(scope: string): void {
    this.fb
      .login({
        scope,
        enable_profile_selector: true,
      })
      .then((response: LoginResponse) => {
        this.getConnectedPages();
      })
      .catch((error: any) => {
        this.dialog.closeAll();
        this.zone.run(() => {
          this.snackbar.open('Facebook auth error', '', {
            duration: 6000,
            panelClass: ['error'],
          });
        });
      });
  }

  getConnectedPages(): void {
    this.fb
      .api('/me/accounts?limit=2000', 'get', { fields: 'name, access_token' })
      .then((response: any) => {
        if (response.data.length) {
          this.fbPages$.next(response.data);
        } else {
          this.dialog.closeAll();
          this.zone.run(() => {
            this.snackbar.open(
              'Sorry, but you have no Facebook pages to select.',
              '',
              {
                duration: 6000,
                panelClass: ['error'],
              }
            );
          });
          this.fb.logout();
        }
      })
      .catch((error: any) => console.error(error));
  }

  createFacebookConnection(connection: any, botId?: string): Observable<any> {
    return this.http.post(
      PortalService.ApiUrl + '/connection/messenger/' + (botId || this.botId),
      connection
    );
  }

  updateFacebookConnection(connection: any, botId?: string): Observable<any> {
    let bot!: string;
    if (botId) {
      bot = botId;
    }
    return this.http.put(
      PortalService.ApiUrl + '/connection/messenger/' + bot,
      connection
    );
  }

  deleteFacebookConnection(botId?: string): Observable<any> {
    let bot!: string;
    if (botId) {
      bot = botId;
    }
    return this.http.delete(
      PortalService.ApiUrl + '/connection/messenger/' + bot
    );
  }

  // DEV
  getFacebookConnection(): Observable<FacebookConnection> {
    return this.http.get<FacebookConnection>(
      'https://api.brn.ai/connection/5N0C70GC3J/messenger/'
    );
  }
}
